import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { cn } from 'support/helpers/cn/cn';
import { WebEDIRowData } from 'support/types/webediTable';
import { SuggestedContentIcon } from 'components/DataTable/cells/WebEDI/components/SuggestedContentIcon/SuggestedContentIcon';
import { shouldShowDiff } from 'components/DataTable/helpers/shouldShowDiff';
import { TimestampDateInput } from 'components/Form/TimestampDateInput/TimestampDateInput';

import { CellProps } from '../../types';
import {
  disableArrowKeys,
  isProcurosAddedValue,
  useCellClassNames,
  useDateConfigurations,
  useIsReadonly,
} from '../cellHelpers';
import { RowStatus } from '../components/RowStatus/RowStatus';
import { InputWrapperCell } from './InputWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;
export const DateCell = ({ fieldId, column, row, table, isHighlighted, mode, diffStatus }: Props) => {
  const { t } = useTranslation();
  const {
    columnDef: { meta: columnMeta, ...columnDef },
    getIsFirstColumn,
    getIsLastColumn,
  } = column;

  const {
    field: { value: inputValue },
  } = useController({ name: fieldId });

  const { formattedValue } = useDateConfigurations({
    value: inputValue,
    mode,
  });

  const { register } = useFormContext();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const fieldState = service.useFieldStateFromAnnotations(fieldId);
  const popoverItem = service.useItemByFieldPath(fieldId);
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const isAdded = isProcurosAddedValue(row);
  const readOnly = useIsReadonly({
    isAdded,
    mode,
    columnMeta,
    rowIndex: row.index,
    fieldId,
    hasError: fieldState === 'error',
  });
  const disabled = isSubmitting;
  const hasDiff = shouldShowDiff(diffStatus, fieldState);

  const cellClassNames = useCellClassNames({
    isLastColumn: getIsLastColumn(),
    isFirstColumn: getIsFirstColumn(),
    row,
    table,
  });

  const inputClassNames = cn(
    {
      'text-blue-700 focus:text-inherit focus-visible:ring-blue-500! focus-visible:border-blue-500!':
        fieldState === 'pending-validation',
      'bg-warning-50': fieldState === 'warning',
    },
    cellClassNames,
    'text-start',
    columnMeta?.optionalConfig?.isStatusColumn ? 'pl-10!' : undefined,
  );

  return (
    <InputWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      fieldState={fieldState}
      diffStatus={diffStatus}
    >
      <TimestampDateInput
        wrapperClassName="size-full"
        inputWrapperClassName="h-full"
        className={cn(
          'size-full rounded-none border-none! bg-transparent p-3 text-xs shadow-none! outline-hidden!',
          inputClassNames,
        )}
        placeholder={
          !formattedValue && popoverItem?.type === 'required' ? t('common:table.errors.requiredField') : undefined
        }
        title={formattedValue ? String(formattedValue) : undefined}
        value={formattedValue ?? ''}
        readOnly={readOnly}
        disabled={disabled}
        {...register(fieldId)}
        fieldState={fieldState}
        leftIcon={
          columnMeta?.optionalConfig?.isStatusColumn ? (
            <RowStatus
              statusColumn={columnMeta?.optionalConfig?.quantityField}
              currentPath={fieldId}
              isProcurosAdded={isAdded}
              currentColumn={columnDef && 'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
              tooltips={{
                deleted: t('webedi:inputCell.status.deleted'),
                partial: t('webedi:inputCell.status.partial'),
                complete: t('webedi:inputCell.status.completed'),
              }}
            />
          ) : null
        }
        rightAddon={hasDiff ? <SuggestedContentIcon mode="edit" /> : undefined}
        onKeyDown={disableArrowKeys}
        id={fieldId}
        data-field={fieldId}
        data-testid={fieldId}
        data-1p-ignore
      />
    </InputWrapperCell>
  );
};
